import { Injectable, inject } from '@angular/core';
import { SupabaseService } from '../../../shared/services/supabase.service';
import { BrandSlug } from '../shared/brand-slug';
import { EAN } from '../shared/ean';
import { OrgId } from '../shared/ids';
import type { CategorySubscription, SkuQuota, SubscribedSku } from './coverage';
import {
  type BrandCategorySkuCounts,
  EntitlementRepository,
  type EntitlementSnapshot,
  emptyEntitlementSnapshot,
} from './entitlement-repository';

const TABLE = 'portal_org';
const SELECT_COLS =
  'id, brands_own_allow, brands_comp_allow, brands_other_allow, category_brands, skus, max_skus';

type OrgSkuItemRow = {
  s?: 'b' | 'pb' | 'both';
  b?: string | null;
  pb?: string | null;
  a?: 0 | 1;
  m?: 0 | 1;
  v?: 0 | 1;
};

type OrgSkusRow = {
  total?: number;
  total_active?: number;
  items?: Record<string, OrgSkuItemRow>;
};

type OrgRow = {
  id: string;
  brands_own_allow: string[] | null;
  brands_comp_allow: string[] | null;
  brands_other_allow: string[] | null;
  category_brands: Record<string, string[]> | null;
  skus: OrgSkusRow | null;
  max_skus: number | null;
};

@Injectable()
export class SupabaseEntitlementRepository extends EntitlementRepository {
  private readonly supabase = inject(SupabaseService);

  async forOrg(orgId: OrgId): Promise<EntitlementSnapshot> {
    const { data, error } = await this.supabase.client
      .from(TABLE)
      .select(SELECT_COLS)
      .eq('id', orgId)
      .maybeSingle();
    if (error) throw error;
    if (!data) return emptyEntitlementSnapshot(orgId);
    return rowToSnapshot(data as OrgRow);
  }

  async loadBrandCategorySkuCounts(): Promise<BrandCategorySkuCounts> {
    const { data, error } = await this.supabase.client
      .from('portal_references')
      .select('reference_data')
      .eq('reference_key', 'ean_w_data_counts_per_brand_category')
      .maybeSingle();
    if (error) throw error;
    if (!data?.reference_data) return {};
    return data.reference_data as Record<
      string,
      Record<string, { w_data: number; no_data: number }>
    >;
  }
}

function rowToSnapshot(row: OrgRow): EntitlementSnapshot {
  const orgId = OrgId(row.id);
  const { subscribedSkus, catalogBrandByEan } = toSkuMaps(row.skus);
  return {
    entitlement: {
      kind: 'entitlement',
      orgId,
      allowed: {
        own: toBrandSlugSet(row.brands_own_allow),
        competitor: toBrandSlugSet(row.brands_comp_allow),
        other: toBrandSlugSet(row.brands_other_allow),
      },
      coverage: {
        kind: 'coverage',
        orgId,
        categorySubscriptions: toCategorySubscriptions(row.category_brands),
        subscribedSkus,
        quota: toSkuQuota(row.skus, row.max_skus),
      },
    },
    catalogBrandByEan,
  };
}

function toBrandSlugSet(raw: string[] | null): ReadonlySet<BrandSlug> {
  if (!Array.isArray(raw)) return new Set();
  const out = new Set<BrandSlug>();
  for (const s of raw) {
    if (isNonEmptyString(s)) out.add(BrandSlug(s));
  }
  return out;
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

function toCategorySubscriptions(
  raw: Record<string, string[]> | null
): ReadonlyMap<string, CategorySubscription> {
  if (!raw) return new Map();
  const out = new Map<string, CategorySubscription>();
  for (const [category, brands] of Object.entries(raw)) {
    if (!Array.isArray(brands)) continue;
    const included: BrandSlug[] = [];
    for (const b of brands) {
      if (typeof b !== 'string' || !b.trim()) continue;
      try {
        included.push(BrandSlug(b));
      } catch {
        // skip malformed
      }
    }
    out.set(category, { category, includedBrands: included });
  }
  return out;
}

function toSkuMaps(raw: OrgSkusRow | null): {
  subscribedSkus: ReadonlyMap<EAN, SubscribedSku>;
  catalogBrandByEan: ReadonlyMap<EAN, BrandSlug>;
} {
  const subscribedSkus = new Map<EAN, SubscribedSku>();
  const catalogBrandByEan = new Map<EAN, BrandSlug>();
  if (!raw?.items) return { subscribedSkus, catalogBrandByEan };
  for (const [rawEan, item] of Object.entries(raw.items)) {
    const parsed = parseSkuItem(rawEan, item);
    if (!parsed) continue;
    subscribedSkus.set(parsed.sku.ean, parsed.sku);
    if (parsed.brand) catalogBrandByEan.set(parsed.sku.ean, parsed.brand);
  }
  return { subscribedSkus, catalogBrandByEan };
}

/** Wire contract: `s` in {'b','both'} requires a valid `b`; rows violating it are rejected whole. */
function parseSkuItem(
  rawEan: string,
  item: OrgSkuItemRow
): { sku: SubscribedSku; brand: BrandSlug | null } | null {
  let ean: EAN;
  try {
    ean = EAN(rawEan);
  } catch {
    return null;
  }
  if (item.s === 'b' || item.s === 'both') {
    if (!item.b?.trim()) return null;
    let brand: BrandSlug;
    try {
      brand = BrandSlug(item.b);
    } catch {
      return null;
    }
    return { sku: toSku(ean, item.s === 'b' ? 'brand' : 'both', item), brand };
  }
  if (item.s === 'pb') {
    // pb (a numeric key into pb_index, resolving to the Comparison id) is discarded:
    // zero consumers; Comparison membership is answered by ComparisonStore.
    return { sku: toSku(ean, 'segment', item), brand: null };
  }
  return null;
}

function toSku(ean: EAN, cause: SubscribedSku['cause'], item: OrgSkuItemRow): SubscribedSku {
  return {
    ean,
    cause,
    hasSalesData: item.a === 1,
    hasCatalogEntry: item.m === 1,
    listedAtVendor: item.v === 1,
  };
}

function toSkuQuota(raw: OrgSkusRow | null, maxSkus: number | null): SkuQuota {
  return {
    total: raw?.total ?? 0,
    active: raw?.total_active ?? 0,
    max: maxSkus ?? 0,
  };
}
